/**
 * ChunkedRecorder — captures self-contained ~5s webm blobs and gates them
 * by per-chunk RMS so silent / very-quiet chunks never hit the backend.
 *
 * Silent gating is critical because:
 *  - Whisper hallucinates aggressively on near-silence (Issue #2: foreign-
 *    language text, "thanks for watching", repeated tokens).
 *  - Cross-channel crosstalk (e.g. the prospect's voice bleeding into the
 *    closer's mic, or vice versa) is usually 10-20 dB quieter than direct
 *    speech. Gating at ~0.015 RMS keeps each channel honest (Issue #1).
 *
 * Usage:
 *   const rec = new ChunkedRecorder(stream, 5000, blob => upload(blob));
 *   rec.start();
 *   ...
 *   rec.stop();
 */
const DEFAULT_RMS_THRESHOLD = 0.008;
const RMS_SAMPLE_MS = 80;

export class ChunkedRecorder {
  constructor(stream, chunkMs, onChunk, mimeType, opts = {}) {
    this.stream = stream;
    this.chunkMs = chunkMs;
    this.onChunk = onChunk;
    this.mimeType = mimeType || pickMimeType();
    this.silenceThreshold = opts.silenceThreshold ?? DEFAULT_RMS_THRESHOLD;
    this.recorder = null;
    this.interval = null;
    this.running = false;

    this._audioCtx = null;
    this._analyser = null;
    this._source = null;
    this._rmsSamples = [];
    this._rmsTimer = null;
    this._chunks = [];
  }

  start() {
    if (this.running) return;
    this.running = true;
    this._setupVad();
    this._startRecorder();
    this.interval = setInterval(() => this._cycle(), this.chunkMs);
  }

  _setupVad() {
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      this._audioCtx = new Ctx();
      this._source = this._audioCtx.createMediaStreamSource(this.stream);
      this._analyser = this._audioCtx.createAnalyser();
      this._analyser.fftSize = 1024;
      this._source.connect(this._analyser);
      this._rmsTimer = setInterval(() => this._sampleRms(), RMS_SAMPLE_MS);
    } catch (e) {
      // VAD failed (browser without AudioContext for that stream). Fall back
      // to letting every chunk through — backend cleaner is the last line.
      // Log so we know silence-gating is off if transcript quality regresses.
      console.warn("[recorder] VAD setup failed; silence-gating disabled", e);
      this._audioCtx = null;
      this._analyser = null;
    }
  }

  _sampleRms() {
    if (!this._analyser) return;
    const buf = new Float32Array(this._analyser.fftSize);
    this._analyser.getFloatTimeDomainData(buf);
    let sum = 0;
    for (let i = 0; i < buf.length; i++) sum += buf[i] * buf[i];
    const rms = Math.sqrt(sum / buf.length);
    this._rmsSamples.push(rms);
  }

  _chunkHasSpeech() {
    if (!this._rmsSamples.length) return true; // no VAD data → trust it
    // Peak-based: if any sample crosses the threshold the chunk almost
    // certainly contains real speech somewhere. Avg-based gating was too
    // strict and dropped legitimate sentences that had pauses inside.
    let maxRms = 0;
    for (let i = 0; i < this._rmsSamples.length; i++) {
      if (this._rmsSamples[i] > maxRms) maxRms = this._rmsSamples[i];
    }
    return maxRms >= this.silenceThreshold;
  }

  _startRecorder() {
    const opts = this.mimeType ? { mimeType: this.mimeType } : undefined;
    this.recorder = new MediaRecorder(this.stream, opts);
    this._chunks = [];
    this._rmsSamples = [];
    this.recorder.ondataavailable = (e) => {
      if (e.data && e.data.size > 0) this._chunks.push(e.data);
    };
    this.recorder.onstop = () => {
      const blob = this._chunks.length
        ? new Blob(this._chunks, { type: this.mimeType || "audio/webm" })
        : null;
      const hasSpeech = this._chunkHasSpeech();
      this._chunks = [];
      this._rmsSamples = [];
      if (!blob || blob.size < 1500) return; // tiny → drop
      if (!hasSpeech) return; // silent → drop (defends Whisper from hallucinating)
      this.onChunk(blob);
    };
    this.recorder.start();
  }

  _cycle() {
    if (!this.running || !this.recorder) return;
    try {
      this.recorder.stop();
    } catch (e) {
      /* ignore */
    }
    setTimeout(() => {
      if (this.running) this._startRecorder();
    }, 50);
  }

  stop() {
    this.running = false;
    if (this.interval) clearInterval(this.interval);
    this.interval = null;
    if (this._rmsTimer) clearInterval(this._rmsTimer);
    this._rmsTimer = null;
    try {
      this.recorder && this.recorder.state !== "inactive" && this.recorder.stop();
    } catch (e) {
      /* ignore */
    }
    try {
      this.stream.getTracks().forEach((t) => t.stop());
    } catch (e) {
      /* ignore */
    }
    try {
      this._audioCtx && this._audioCtx.close();
    } catch (e) {
      /* ignore */
    }
    this.recorder = null;
    this._audioCtx = null;
    this._analyser = null;
    this._source = null;
  }
}

function pickMimeType() {
  const candidates = [
    "audio/webm;codecs=opus",
    "audio/webm",
    "audio/mp4",
    "audio/ogg;codecs=opus",
  ];
  for (const c of candidates) {
    try {
      if (window.MediaRecorder && MediaRecorder.isTypeSupported(c)) return c;
    } catch (e) {
      /* ignore */
    }
  }
  return "";
}

/** Request microphone (closer) audio stream. */
export const getMicStream = () =>
  navigator.mediaDevices.getUserMedia({
    audio: {
      echoCancellation: true,
      noiseSuppression: true,
      autoGainControl: true,
    },
    video: false,
  });

/**
 * Request a tab/system audio stream via getDisplayMedia.
 * The browser requires the user to enable "Share audio" when picking the tab/window.
 * We discard the video track and keep only audio.
 */
export const getSystemAudioStream = async () => {
  const display = await navigator.mediaDevices.getDisplayMedia({
    video: true, // required, but we strip it
    audio: true,
  });
  display.getVideoTracks().forEach((t) => {
    t.stop();
    display.removeTrack(t);
  });
  if (display.getAudioTracks().length === 0) {
    display.getTracks().forEach((t) => t.stop());
    throw new Error(
      "No audio track. When sharing, you must check 'Share tab audio' (or 'Share system audio')."
    );
  }
  return display;
};
